import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export default function CompareModal({ open, models, onClose }) {
  const [a, b] = models || [];
  const specKeys = a && b ? [...new Set([...Object.keys(a.specs), ...Object.keys(b.specs)])] : [];
  const cheaper = a && b ? (a.priceRaw <= b.priceRaw ? a.id : b.id) : null;

  return (
    <AnimatePresence>
      {open && a && b && (
        <motion.div
          data-testid="compare-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="compare-modal-backdrop" />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-4xl bg-[#0D1822] border border-white/10 clip-card overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <button
              data-testid="compare-modal-close-button"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center bg-black/50 backdrop-blur-md border border-white/15 text-white hover:border-[#FFFFFF] transition-colors"
              aria-label="Close comparison"
            >
              <X size={18} />
            </button>

            <div className="grid grid-cols-2">
              {[a, b].map((m) => (
                <div key={m.id} data-testid={`compare-col-${m.id}`} className="p-5 sm:p-8 border-b border-white/10 first:border-r">
                  <div className="h-28 sm:h-40 card-spotlight mb-4">
                    <img src={m.image} alt={m.name} className={`w-full h-full ${m.fit === "cover" ? "object-cover" : "object-contain p-3"}`} />
                  </div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#FFFFFF]">{m.badge}</p>
                  <h3 className="font-display font-extrabold text-lg sm:text-2xl text-white tracking-tight mt-1">{m.name}</h3>
                  <p className={`mt-2 font-display font-bold text-base sm:text-xl ${cheaper === m.id ? "text-[#FFFFFF]" : "text-slate-300"}`}>
                    {m.price}
                    {cheaper === m.id && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] border border-[#FFFFFF]/50 px-2 py-0.5 align-middle">Lower price</span>}
                  </p>
                </div>
              ))}
            </div>

            <div className="p-5 sm:p-8">
              <table data-testid="compare-table" className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="py-3.5 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 w-28 sm:w-40">Category</td>
                    <td className="py-3.5 pr-4 text-slate-200">{a.category}</td>
                    <td className="py-3.5 text-slate-200">{b.category}</td>
                  </tr>
                  {specKeys.map((k) => (
                    <tr key={k} className="border-b border-white/5">
                      <td className="py-3.5 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 align-top">{k.replace(/_/g, " ")}</td>
                      <td className="py-3.5 pr-4 text-slate-300 align-top">{a.specs[k] || "—"}</td>
                      <td className="py-3.5 text-slate-300 align-top">{b.specs[k] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
